import { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { RoadmapResponse, RoadmapDimension } from "../../api/client";
import EstimateCard from "./EstimateCard";
import OutcomePanel from "../outcome/OutcomePanel";

interface RoadmapIntroProps {
  roadmap: RoadmapResponse;
}

// Spec §7 — H1 shows a personalized weeks range from the deterministic
// estimator when available, falling back to "Your roadmap". The four-phase
// order is the roadmap's identity. Lede is computed from dimensions: identify
// the strongest tier ("strong") and the dimensions still being built
// ("gap"/"building"), then assert the products-do-the-work model. Meta row
// shows pace, date range, last revised.
export default function RoadmapIntro({ roadmap }: RoadmapIntroProps) {
  const { t } = useTranslation("roadmap");
  const est = roadmap.estimate ?? null;
  const [track, setTrack] = useState<"knowledge" | "portfolio">(
    est?.recommended_track ?? "portfolio"
  );
  const weeks = est ? est[track].weeks : null;

  const lede = composeLede(roadmap.dimensions, t);
  const startedAt = new Date(roadmap.started_at);
  const targetAt = new Date(roadmap.target_at);
  const lastRevisedAt = new Date(roadmap.last_revised_at);

  return (
    <Box component="section" sx={{ mb: 5 }}>
      <Typography
        variant="overline"
        color="secondary.main"
        sx={{ fontWeight: 600, letterSpacing: "0.08em", display: "block", mb: 1 }}
      >
        {roadmap.archetype.name}
      </Typography>

      <Typography
        variant="h3"
        component="h1"
        sx={{
          fontWeight: 500,
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          mb: 2,
          fontSize: { xs: "1.75rem", md: "2.25rem" },
        }}
      >
        {weeks
          ? t("RoadmapIntro.weeksHeadline", { low: weeks[0], high: weeks[1] })
          : t("RoadmapIntro.defaultHeadline")} · {t("RoadmapIntro.productSequence")}
      </Typography>

      {est && (
        <EstimateCard estimate={est} track={track} onTrackChange={setTrack} />
      )}

      {roadmap.assessment && roadmap.estimate && (
        <OutcomePanel
          skillMatrix={(roadmap.assessment.q4_skill_matrix as Record<string, number>) ?? {}}
          targetRole={(roadmap.assessment.q5_target_role as string) ?? "agentic_eng"}
          seniority={(roadmap.assessment.q6_target_seniority as string) ?? "mid_senior"}
          track={track}
        />
      )}

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ lineHeight: 1.6, mb: 2.5, maxWidth: 720 }}
      >
        {lede}
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        flexWrap="wrap"
        useFlexGap
        divider={
          <Typography component="span" variant="caption" color="text.secondary">·</Typography>
        }
        sx={{ color: "text.secondary" }}
      >
        <MetaItem label={t("RoadmapIntro.metaPace")} value={roadmap.overall.pace} />
        <MetaItem
          label={t("RoadmapIntro.metaPlanWindow")}
          value={`${formatShortDate(startedAt)} → ${formatShortDate(targetAt)}`}
        />
        <MetaItem label={t("RoadmapIntro.metaLastRevised")} value={formatRelative(lastRevisedAt, t)} />
      </Stack>
    </Box>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <Typography variant="caption" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box
        component="span"
        sx={{
          fontFamily: "monospace",
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "text.secondary",
          opacity: 0.7,
        }}
      >
        {label}
      </Box>
      <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
        {value}
      </Box>
    </Typography>
  );
}

function composeLede(dimensions: RoadmapDimension[], t: TFunction): string {
  // Dimension titles mix adjectives (Technical, Interview) and nouns
  // (Portfolio, Network, Narrative). Sentence frames where the title sits
  // in topic position ("strong on X", "focused on X") work for both;
  // frames where it sits in subject position ("X is strong") only work
  // for nouns.
  const strong = dimensions.filter((d) => d.tier === "strong").map((d) => d.title.toLowerCase());
  const gaps = dimensions
    .filter((d) => d.tier === "gap" || d.tier === "building")
    .sort((a, b) => a.score - b.score)
    .slice(0, 2)
    .map((d) => d.title.toLowerCase());

  const first = (() => {
    if (strong.length > 0 && gaps.length > 0) {
      return t("RoadmapIntro.ledeStrongAndGaps", {
        strong: joinNicely(strong, t),
        gaps: joinNicely(gaps, t),
      });
    }
    if (gaps.length > 0) {
      return t("RoadmapIntro.ledeGapsOnly", { gaps: joinNicely(gaps, t) });
    }
    if (strong.length > 0) {
      return t("RoadmapIntro.ledeStrongOnly", { strong: joinNicely(strong, t) });
    }
    return t("RoadmapIntro.ledeFallback");
  })();

  const second = t("RoadmapIntro.ledeSecond");

  return `${first} ${second}`;
}

function joinNicely(items: string[], t: TFunction): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  const and = t("RoadmapIntro.listAnd");
  if (items.length === 2) return `${items[0]} ${and} ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, ${and} ${items[items.length - 1]}`;
}

function formatShortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatRelative(d: Date, t: TFunction): string {
  const ms = Date.now() - d.getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days <= 0) return t("RoadmapIntro.today");
  if (days === 1) return t("RoadmapIntro.yesterday");
  if (days < 7) return t("RoadmapIntro.daysAgo", { count: days });
  if (days < 30) return t("RoadmapIntro.weeksAgo", { count: Math.floor(days / 7) });
  return t("RoadmapIntro.monthsAgo", { count: Math.floor(days / 30) });
}
